import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { DetailShell, Section, Icon, useDetailReadOnly } from './detail/DetailShell';
import { SearchableSelect } from './ui/SearchableSelect';

// KAMPANYA YETKİLERİ — DETAY (menü 9120).
//
// Bir YETKİ HEDEFİNİN (tek temsilci / temsilci grubu / temsilci tipi) 13 detayı, 5 sekmede
// tek yerden kaydedilir. Kayıt hedef bazında TAMAMEN yeniden yazılır (full-replace) —
// PermissionProfiles `PUT /Rights` ile aynı desen; bu yüzden "tek kural sil" diye bir işlem yok.
//
// #282: mevcut hedef İZLEME modunda açılır; düzenleme `?edit=1` ister (DetailShell halleder).
// Yeni kayıt (/new) doğrudan düzenlenebilir.
const API = '/api/campaign-authorizations';
const LIST_ROUTE = '/campaign-authorizations';

// Sayısal değer aynı zamanda ÖZGÜLLÜK sırasıdır (büyük olan daha özel).
const TARGET = { TYPE: 1, GROUP: 2, REP: 3 };
const CRITERIA = [
  { key: TARGET.REP, label: 'Tek Temsilci', icon: 'user', hint: 'Bir kişiye özel istisna' },
  { key: TARGET.GROUP, label: 'Temsilci Grubu', icon: 'users', hint: 'Grup politikası' },
  { key: TARGET.TYPE, label: 'Temsilci Tipi', icon: 'layers', hint: 'Tip tabanı' },
];
const TARGET_LABEL = {
  [TARGET.REP]: 'Tek Temsilci',
  [TARGET.GROUP]: 'Temsilci Grubu',
  [TARGET.TYPE]: 'Temsilci Tipi',
};

const MODE = { NONE: 0, LIMITED: 1, FULL: 2 };
// #818 Yürürlükteki yetkinin hangi eksenden geldiği (backend `source` alanı = AuthorizationTargetType).
const SOURCE_LABEL = { 1: 'Temsilci tipi', 2: 'Temsilci grubu', 3: 'Temsilciye özel' };
const MODES = [
  { key: MODE.NONE, label: 'Yetki Yok', tone: 'none' },
  { key: MODE.LIMITED, label: 'Sınırlı', tone: 'limited' },
  { key: MODE.FULL, label: 'Tam Yetki', tone: 'full' },
];

// Üst sınır birimleri (backend AuthorizationCapType).
const CAP = { PERCENT: 1, AMOUNT: 2, QUANTITY: 3 };
const CAP_SUFFIX = { [CAP.PERCENT]: '%', [CAP.AMOUNT]: '₺', [CAP.QUANTITY]: 'adet' };

// Üst sınırın sayıldığı kapsam — yalnız bazı detaylarda (bedelsiz) anlamlı.
const SCOPE = { DOCUMENT: 0, CAMPAIGN: 1 };

const TAB_ICONS = {
  kalem_iskonto: 'list',
  kalem_tutar: 'wallet',
  belge_alti: 'layers',
  fiyat: 'tag',
  kampanya: 'box',
};

function unwrap(p) { return p?.data ?? p?.Data ?? p; }

export function CampaignAuthorizationDetailPage() {
  const { targetType: routeType, targetId: routeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isNew = (location.pathname || '').endsWith('/new');

  usePageMeta(
    isNew ? 'Yeni Kampanya Yetkisi' : 'Kampanya Yetkisi',
    'Temsilciye, gruba ya da tipe iskonto, fiyat ve kampanya yetkisi tanımlayın.',
  );

  const [catalog, setCatalog] = useState([]);
  const [tab, setTab] = useState(null);

  const [targetType, setTargetType] = useState(isNew ? TARGET.REP : Number(routeType) || TARGET.REP);
  const [targetId, setTargetId] = useState(isNew ? '' : (routeId || ''));
  // Listeden gelindiyse hedef adı state ile taşınır; doğrudan URL'de List ucundan çözülür.
  const [targetLabel, setTargetLabel] = useState(location.state?.row?.targetName || '');
  const [groups, setGroups] = useState([]);
  const [types, setTypes] = useState([]);

  // detailKey -> { mode, capType, capValue, capScope }
  const [rules, setRules] = useState({});
  const [configured, setConfigured] = useState(false);
  // #818 Yürürlükteki (efektif) yetki — yalnız TEMSİLCİ hedefinde anlamlı: üç eksen (Temsilci > Grup >
  // Tip) çözülüp EN ÖZEL kayıt kazanıyor, ama yönetici bu ekranda yalnız DÜZENLEDİĞİ ekseni görüyordu.
  // "Yetki verdim ama işe yaramıyor" şikâyetinin kaynağı bu: grup düzeyinde %20 verirken temsilci
  // düzeyindeki eski %10 kaydı sessizce kazanıyor.
  const [effective, setEffective] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 4000); };

  const details = useMemo(() => catalog.flatMap((t) => t.details || []), [catalog]);
  const detailByKey = useMemo(() => Object.fromEntries(details.map((d) => [d.key, d])), [details]);

  // ---- Katalog + grup/tip listeleri (bir kez) ----
  useEffect(() => {
    (async () => {
      try {
        const [catRes, grpRes, typRes] = await Promise.all([
          fetch(`${API}/Catalog`, { headers: authHeaders() }),
          fetch(`${API}/Targets?targetType=${TARGET.GROUP}`, { headers: authHeaders() }),
          fetch(`${API}/Targets?targetType=${TARGET.TYPE}`, { headers: authHeaders() }),
        ]);
        const cat = unwrap(await catRes.json()) || [];
        setCatalog(cat);
        setTab(cat[0]?.key ?? null);
        setGroups(unwrap(await grpRes.json()) || []);
        setTypes(unwrap(await typRes.json()) || []);
      } catch {
        showToast('error', 'Katalog yüklenemedi');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ---- Hedefin kuralları ----
  const loadTarget = useCallback(async (type, id) => {
    if (!id) { setRules({}); setConfigured(false); return; }
    setLoadingTarget(true);
    try {
      const res = await fetch(`${API}?targetType=${type}&targetId=${id}`, { headers: authHeaders() });
      const body = unwrap(await res.json());
      const next = {};
      (body?.items || []).forEach((it) => {
        next[it.detailKey] = {
          mode: it.mode,
          capType: it.capType ?? null,
          capValue: it.capValue ?? null,
          capScope: it.capScope ?? SCOPE.DOCUMENT,
        };
      });
      setRules(next);
      setConfigured(Object.keys(next).length > 0);
    } catch {
      showToast('error', 'Yetkiler yüklenemedi');
    } finally {
      setLoadingTarget(false);
    }
  }, []);

  useEffect(() => { loadTarget(targetType, targetId); }, [targetType, targetId, loadTarget]);

  // #818 Temsilci hedefinde yürürlükteki yetkiyi çek (kaydettikçe tazelenir).
  useEffect(() => {
    if (targetType !== TARGET.REP || !targetId) { setEffective(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/Effective?representativeId=${targetId}`, { headers: authHeaders() });
        if (!res.ok) { if (!cancelled) setEffective(null); return; }
        const body = unwrap(await res.json());
        if (!cancelled) setEffective(body?.items || []);
      } catch {
        if (!cancelled) setEffective(null);
      }
    })();
    return () => { cancelled = true; };
  }, [targetType, targetId, rules]);

  // Doğrudan URL ile gelindiyse (sayfa yenileme) hedef adı state'te yok — liste ucundan çöz.
  // Ayrı bir "hedefi id ile getir" ucu açmaya gerek yok; List zaten adı taşıyor.
  useEffect(() => {
    if (isNew || targetLabel || !targetId) return;
    (async () => {
      try {
        const url = `${API}/List?$filter=TargetId eq ${targetId}&$top=1`;
        const res = await fetch(url, { headers: authHeaders() });
        const body = await res.json();
        const row = (body?.value || [])[0];
        if (row) setTargetLabel(row.targetCode ? `${row.targetName} · ${row.targetCode}` : row.targetName);
      } catch {
        /* ad çözülemezse ekran yine çalışır — başlıkta kriter adı görünür */
      }
    })();
  }, [isNew, targetId, targetLabel]);

  const pickCriterion = (key) => {
    if (key === targetType) return;
    setTargetType(key);
    setTargetId('');
    setTargetLabel('');
    setRules({});
    setConfigured(false);
  };

  // Temsilci araması SUNUCU taraflıdır — binlerce temsilcide tüm listeyi indirmek anlamsız.
  const searchReps = useCallback(async (q) => {
    const res = await fetch(`${API}/Targets?targetType=${TARGET.REP}&q=${encodeURIComponent(q || '')}`, { headers: authHeaders() });
    const list = unwrap(await res.json()) || [];
    return list.map((r) => ({ value: r.id, label: r.code ? `${r.name} · ${r.code}` : r.name }));
  }, []);

  // Kaydı olmayan detay "Yetki Yok"tur (güvenli taban) — okuma tarafında da varsayılan None.
  const ruleOf = (key) => rules[key] ?? { mode: MODE.NONE, capType: null, capValue: null, capScope: SCOPE.DOCUMENT };

  const setMode = (key, mode) => {
    const def = detailByKey[key];
    setRules((cur) => {
      const prev = cur[key] ?? {};
      if (mode !== MODE.LIMITED) {
        return { ...cur, [key]: { mode, capType: null, capValue: null, capScope: SCOPE.DOCUMENT } };
      }
      // Sınırlı'ya geçince birim seçili değilse ilk izinli birim alınır; değeri kullanıcı girer.
      const capType = prev.capType ?? def?.allowedCapTypes?.[0] ?? CAP.PERCENT;
      return {
        ...cur,
        [key]: { mode, capType, capValue: prev.capValue ?? '', capScope: prev.capScope ?? SCOPE.DOCUMENT },
      };
    });
  };

  const patch = (key, fields) => setRules((cur) => ({ ...cur, [key]: { ...ruleOf(key), ...fields } }));

  // ---- Kaydet ----
  const persist = async () => {
    if (!targetId) { showToast('error', 'Önce bir yetki hedefi seçin'); return; }

    // Sınırlı için üst sınır > 0 ZORUNLU — sınırsız bir "Sınırlı", Tam Yetki'nin yanlış adıdır.
    const missing = Object.entries(rules).filter(([, r]) => r.mode === MODE.LIMITED && !(Number(r.capValue) > 0));
    if (missing.length > 0) {
      const names = missing.map(([k]) => detailByKey[k]?.name || k).join(', ');
      showToast('error', `Sınırlı seçilen şu detaylarda üst sınır girilmeli: ${names}`);
      return;
    }

    setSaving(true);
    try {
      // KATALOĞUN TAMAMI gönderilir — "Yetki Yok" olanlar dahil.
      //
      // Yalnız izin verilenleri göndermek, niyeti TERSİNE çevirirdi: 13 detayı da Yetki Yok yapıp
      // kaydeden yönetici sıfır satır üretir, sıfır satır ise "bu hedef hiç tanımlanmamış" demektir
      // ve sunucu o temsilciyi denetlemez (canlıya çıkış kapısı, bkz. IsConfigured) → temsilci
      // kilitlenmek yerine SINIRSIZ iskonto verirdi. Hedefi boşaltmak istiyorsan listedeki
      // "Yetkileri Kaldır" vardır; o, denetimi kapattığını açıkça söyler.
      const items = details.map((d) => {
        const r = ruleOf(d.key);
        return {
          tab: d.tab ?? undefined,
          detailKey: d.key,
          mode: r.mode,
          capType: r.mode === MODE.LIMITED ? r.capType : null,
          capValue: r.mode === MODE.LIMITED ? Number(r.capValue) : null,
          capScope: r.mode === MODE.LIMITED ? r.capScope : null,
        };
      });

      const res = await fetch(API, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ targetType, targetId, items }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Yetkiler kaydedilemedi');

      setConfigured(items.length > 0);
      showToast('success', 'Yetkiler kaydedildi');
      // Konvansiyon (#162): başarılı kayıttan sonra listeye dön.
      setTimeout(() => navigate(LIST_ROUTE), 400);
    } catch (err) {
      showToast('error', err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const activeTab = catalog.find((t) => t.key === tab);

  // Sekme etiketinde kaç detayda yetki verildiği görünür (DetailShell'in rozet desteği yok) —
  // yönetici hangi sekmeye dokunduğunu sekmeye girmeden görsün.
  const grantedCount = (t) => (t.details || []).filter((d) => ruleOf(d.key).mode !== MODE.NONE).length;

  // İzleme modunda DetailShell bu aksiyonları render ETMEZ (Kapat + Düzenle koyar).
  const actions = (
    <button className="dps-btn primary" onClick={persist} disabled={saving || !targetId}>
      <Icon name="check" size={16} /> {saving ? 'Kaydediliyor…' : 'Kaydet'}
    </button>
  );

  const crumbTargetLabel = targetLabel || (isNew ? 'Yeni Yetki' : TARGET_LABEL[targetType] || 'Yetki');

  return (
    <DetailShell
      crumb={[
        { label: 'Kampanya' },
        { label: 'Kampanya Yetkileri', to: LIST_ROUTE },
        { label: crumbTargetLabel, current: true },
      ]}
      tabs={(catalog || []).map((t) => {
        const n = targetId ? grantedCount(t) : 0;
        return {
          key: t.key,
          label: n > 0 ? `${t.name} (${n})` : t.name,
          icon: TAB_ICONS[t.key] || 'list',
          title: t.hint,
        };
      })}
      activeTab={tab}
      onTab={setTab}
      actions={actions}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="shield" title="Yetki Kriteri" subtitle="Bu yetkiler kime uygulanacak?">
          <TargetPicker
            isNew={isNew}
            targetType={targetType}
            targetId={targetId}
            targetLabel={targetLabel}
            groups={groups}
            types={types}
            searchReps={searchReps}
            onPickCriterion={pickCriterion}
            onPickTarget={(id, label) => { setTargetId(id || ''); setTargetLabel(label || ''); }}
          />

          {/* Çözüm önceliği görünür olmalı: yönetici gruba kural yazdığında, o gruptaki bir
              temsilcinin KENDİ kaydının bunu ezeceğini bilmeli. */}
          <p style={S.note}>
            <Icon name="info" size={14} />
            Belge kesilirken en özel kayıt kazanır: <b>Tek Temsilci &gt; Temsilci Grubu &gt; Temsilci Tipi</b>.
          </p>

          {targetId && !configured && !loadingTarget && (
            <p style={S.warn}>
              <Icon name="info" size={14} />
              Bu hedefin henüz hiç yetkisi tanımlı değil — <b>denetim uygulanmıyor</b> (bugünkü davranış sürüyor).
              İlk kuralı kaydettiğiniz anda denetim başlar ve <b>tanımlamadığınız her detay “Yetki Yok” sayılır</b>.
            </p>
          )}
        </Section>

        {/* #818 YÜRÜRLÜKTEKİ YETKİ — belge kesilirken GERÇEKTEN uygulanan küme. Bu ekranda düzenlenen
            kayıt her zaman kazanmaz: en ÖZEL eksen (Temsilci > Grup > Tip) kazanır. Panel, kazananı ve
            hangi eksenden geldiğini gösterir; düzenlenen eksen kaybediyorsa satır uyarıyla işaretlenir.
            Böylece "yetki verdim ama sipariş girişinde işe yaramıyor" sorusu ekranda cevaplanır. */}
        {targetType === TARGET.REP && targetId && Array.isArray(effective) && (
          <Section
            icon="shield"
            title="Yürürlükteki Yetki"
            subtitle="Belge kesilirken gerçekten uygulanan küme — en özel tanım kazanır"
          >
            {effective.length === 0 ? (
              <div style={S.empty}>
                Bu temsilci için hiçbir eksende tanım yok — denetim uygulanmıyor.
              </div>
            ) : (
              <div style={S.rows}>
                {effective.map((e) => {
                  const detail = catalog.flatMap((t) => t.details || []).find((d) => d.key === e.detailKey);
                  const mine = rules[e.detailKey];
                  // Düzenlenen eksen kazanıyor mu? (source, seçili hedef ekseniyle aynı mı)
                  const overridden = mine != null && e.source != null && e.source !== targetType;
                  const modeLabel = (MODES.find((m) => m.key === e.mode) || {}).label || '—';
                  const cap = e.mode === MODE.LIMITED && e.capValue != null
                    ? (e.capType === 1 ? ` · en fazla %${e.capValue}` : ` · en fazla ${e.capValue}`)
                    : '';
                  return (
                    <div
                      key={e.detailKey}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                        borderBottom: '1px solid var(--dps-line, #eef1f5)', fontSize: 13.5,
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>{detail?.name || e.detailKey}</span>
                      <strong>{modeLabel}{cap}</strong>
                      <span style={{ color: 'var(--dps-ink-3, #64748b)', fontSize: 12.5 }}>
                        {SOURCE_LABEL[e.source] || '—'}
                      </span>
                      {overridden && (
                        <span
                          title="Bu detayda başka bir eksendeki tanım daha özel olduğu için kazanıyor; buradaki değeriniz uygulanmıyor."
                          style={{
                            padding: '2px 8px', borderRadius: 999, fontSize: 11.5, fontWeight: 700,
                            background: 'rgba(253,171,61,0.16)', color: '#92400e',
                          }}
                        >
                          buradaki tanım geçersiz
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        )}

        {!targetId ? (
          <Section icon="user" title="Yetkiler" subtitle="Yetkileri görmek için önce bir hedef seçin">
            <div style={S.empty}>Yukarıdan bir temsilci, grup ya da tip seçin.</div>
          </Section>
        ) : activeTab && (
          <Section icon={TAB_ICONS[activeTab.key] || 'list'} title={activeTab.name} subtitle={activeTab.hint}>
            {loadingTarget ? (
              <div style={S.empty}>Yükleniyor…</div>
            ) : (
              <div style={S.rows}>
                {(activeTab.details || []).map((d) => (
                  <AuthorizationRow
                    key={d.key}
                    detail={d}
                    rule={ruleOf(d.key)}
                    onMode={(m) => setMode(d.key, m)}
                    onPatch={(f) => patch(d.key, f)}
                  />
                ))}
              </div>
            )}

            <div style={S.legend}>
              <span><i style={{ ...S.dot, background: C.none }} /> Yetki Yok — alan kilitli</span>
              <span><i style={{ ...S.dot, background: C.limited }} /> Sınırlı — üst sınıra kadar</span>
              <span><i style={{ ...S.dot, background: C.full }} /> Tam Yetki — sınırsız</span>
            </div>
          </Section>
        )}
      </div>

      {/* Toast bileşeni `{ toast }` prop'u alır (type/message/onClose DEĞİL) — eski tek-sayfa
          sürümünde yanlış prop verildiği için toast'lar hiç görünmüyordu. */}
      <Toast toast={toast} />
    </DetailShell>
  );
}

// ──────────────────────────────────────────────────────── Hedef seçici
//
// Mevcut kayıtta hedef DEĞİŞTİRİLEMEZ: hedef (tip + id) bu kaydın kimliğidir; değiştirmek
// "başka bir hedefi düzenlemek" olurdu. Düzenlemede sadece okunur özet gösterilir.

function TargetPicker({ isNew, targetType, targetId, targetLabel, groups, types, searchReps, onPickCriterion, onPickTarget }) {
  const readOnly = useDetailReadOnly();

  if (!isNew) {
    const name = targetLabel
      || groups.find((g) => g.id === targetId)?.name
      || types.find((t) => t.id === targetId)?.name
      || '—';
    return (
      <div style={S.lockedTarget}>
        <span style={S.lockedBadge}>{TARGET_LABEL[targetType]}</span>
        <b style={{ fontSize: 15 }}>{name}</b>
        <span style={S.lockedHint}>
          <Icon name="info" size={13} /> Hedef değiştirilemez — başka bir hedef için listeden yeni yetki ekleyin.
        </span>
      </div>
    );
  }

  return (
    <>
      <div style={S.critSeg}>
        {CRITERIA.map((c) => (
          <button
            key={c.key}
            type="button"
            disabled={readOnly}
            style={{ ...S.critBtn, ...(targetType === c.key ? S.critBtnOn : {}), ...(readOnly ? S.disabled : {}) }}
            onClick={() => onPickCriterion(c.key)}
            title={c.hint}
          >
            <Icon name={c.icon} size={15} /> {c.label}
          </button>
        ))}
      </div>

      <div style={S.picker}>
        {targetType === TARGET.REP && (
          <label style={S.field}>
            <span style={S.fieldLbl}>Temsilci</span>
            <SearchableSelect
              value={targetId}
              selectedLabel={targetLabel}
              onChange={(v, item) => onPickTarget(v, item?.label)}
              fetchOptions={searchReps}
              placeholder="Temsilci ara…"
              pageSize={50}
              disabled={readOnly}
            />
          </label>
        )}
        {targetType === TARGET.GROUP && (
          <label style={S.field}>
            <span style={S.fieldLbl}>Temsilci Grubu</span>
            <select
              style={S.select}
              value={targetId}
              disabled={readOnly}
              onChange={(e) => onPickTarget(e.target.value, groups.find((g) => g.id === e.target.value)?.name)}
            >
              <option value="">Seçiniz…</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </label>
        )}
        {targetType === TARGET.TYPE && (
          <label style={S.field}>
            <span style={S.fieldLbl}>Temsilci Tipi</span>
            <select
              style={S.select}
              value={targetId}
              disabled={readOnly}
              onChange={(e) => onPickTarget(e.target.value, types.find((t) => t.id === e.target.value)?.name)}
            >
              <option value="">Seçiniz…</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
        )}
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────── Yetki satırı

function AuthorizationRow({ detail, rule, onMode, onPatch }) {
  // İzleme modu (#282) ve Update hakkı yokluğu DetailShell'den context ile gelir —
  // bu satırlar Field* bileşeni olmadığı için kilidi elle uygularız.
  const disabled = useDetailReadOnly();
  const limited = rule.mode === MODE.LIMITED;
  const allowed = detail.allowedCapTypes || [CAP.PERCENT];
  const tone = rule.mode === MODE.FULL ? 'full' : limited ? 'limited' : 'none';

  return (
    <div style={{ ...S.row, borderLeft: `4px solid ${C[tone]}` }}>
      <div style={S.rowName}>
        <b>{detail.name}</b>
        <span>{detail.description}</span>
      </div>

      <div style={S.tri}>
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            disabled={disabled}
            onClick={() => onMode(m.key)}
            style={{
              ...S.triBtn,
              ...(rule.mode === m.key ? { background: C[m.tone], color: '#fff' } : {}),
              ...(disabled ? S.triBtnDisabled : {}),
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Sınır alanı: satırlar arası hizanın bozulmaması için tek parça DEĞİL, iki SABİT yuva.
          "belge/kampanya başına" seçicisi yalnız bazı detaylarda (bedelsiz) var; yuvası her
          satırda rezerve edilir, yoksa boş bırakılır. Aksi halde Kampanya sekmesinde o satır
          diğerlerinden geniş olur ve mod düğmeleri kayar. */}
      <div style={S.cap}>
        <div style={{ ...S.capMain, visibility: limited ? 'visible' : 'hidden' }}>
          <span style={S.capLbl}>En fazla</span>
          <span style={S.capBox}>
            <input
              style={S.capInput}
              inputMode="decimal"
              value={rule.capValue ?? ''}
              disabled={disabled}
              onChange={(e) => onPatch({ capValue: e.target.value })}
            />
            {/* Birden çok birim izinliyse (ör. manuel fiyat: % veya ₺) kullanıcı seçer;
                tek birim varsa sabit etiket gösterilir — gereksiz dropdown koymayız. */}
            {allowed.length > 1 ? (
              <select
                style={S.capUnitSelect}
                value={rule.capType ?? allowed[0]}
                disabled={disabled}
                onChange={(e) => onPatch({ capType: Number(e.target.value) })}
              >
                {allowed.map((c) => <option key={c} value={c}>{CAP_SUFFIX[c]}</option>)}
              </select>
            ) : (
              <span style={S.capUnit}>{CAP_SUFFIX[allowed[0]]}</span>
            )}
          </span>
        </div>

        <div style={S.scopeSlot}>
          {detail.allowsCapScope && (
            <select
              style={{ ...S.scopeSelect, visibility: limited ? 'visible' : 'hidden' }}
              value={rule.capScope ?? SCOPE.DOCUMENT}
              disabled={disabled}
              onChange={(e) => onPatch({ capScope: Number(e.target.value) })}
            >
              <option value={SCOPE.DOCUMENT}>belge başına</option>
              <option value={SCOPE.CAMPAIGN}>kampanya başına</option>
            </select>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────── Stiller
// Not: `dps-btn primary` yalnız DetailShell'in .dps kökü içinde doğru renklenir; bu sayfanın
// tüm butonları o ağacın içindedir. Satır stilleri inline — c360/DetailShell'de eşdeğeri yok.

const C = {
  none: '#e07b73',      // soft kırmızı — dikkat çeker ama alarm değildir
  limited: '#b57d18',   // amber
  full: '#3f9d55',      // yeşil
};

const S = {
  critSeg: { display: 'inline-flex', background: '#eef2f6', borderRadius: 11, padding: 4, gap: 3, marginBottom: 14 },
  critBtn: { border: 'none', background: 'transparent', color: '#3c4a5e', padding: '9px 16px', fontSize: 13, borderRadius: 8, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 7 },
  critBtnOn: { background: '#16233f', color: '#fff' },
  disabled: { cursor: 'not-allowed', opacity: 0.6 },

  lockedTarget: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  lockedBadge: { background: '#eef2f6', color: '#3c4a5e', fontWeight: 700, fontSize: 12, borderRadius: 8, padding: '6px 10px' },
  lockedHint: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#8b96a6' },

  picker: { maxWidth: 420 },
  field: { display: 'flex', flexDirection: 'column', gap: 7 },
  fieldLbl: { fontSize: 12.5, color: '#3c4a5e', fontWeight: 600 },
  select: { background: '#f2f6f9', border: '1px solid #e9eef3', borderRadius: 10, padding: '12px 14px', fontSize: 14, color: '#18233b', outline: 'none' },

  note: { display: 'flex', alignItems: 'center', gap: 7, margin: '14px 0 0', fontSize: 12.5, color: '#8b96a6' },
  warn: { display: 'flex', alignItems: 'flex-start', gap: 7, margin: '10px 0 0', padding: '10px 14px', fontSize: 12.5, color: '#8a6d1f', background: '#fbf1da', border: '1px solid #eeddb4', borderRadius: 10, lineHeight: 1.5 },

  empty: { padding: '28px 4px', color: '#8b96a6', fontSize: 13 },

  rows: { display: 'flex', flexDirection: 'column' },
  // SABİT IZGARA: [detay adı | mod düğmeleri | sınır] — üç kolon da her satırda aynı genişlikte.
  // (Flex iken `bedelsiz` satırındaki fazladan kapsam seçicisi mod düğmelerini kaydırıyordu.)
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) max-content 340px',
    alignItems: 'center',
    columnGap: 16,
    padding: '14px 16px',
    borderTop: '1px solid #eef2f6',
  },
  rowName: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },

  tri: { display: 'inline-flex', background: '#eef2f6', borderRadius: 10, padding: 3, gap: 2, justifySelf: 'end' },
  triBtn: { border: 'none', background: 'transparent', color: '#3c4a5e', padding: '8px 13px', fontSize: 12, borderRadius: 7, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' },
  triBtnDisabled: { cursor: 'not-allowed', opacity: 0.6 },

  // Sınır kolonu: "en fazla + değer/birim" yuvası ile kapsam yuvası ayrı ve SABİT genişlikte;
  // kapsam seçicisi olmayan satırlarda yuva boş kalır ama yerini korur → hiza bozulmaz.
  cap: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 132px', alignItems: 'center', columnGap: 8 },
  capMain: { display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' },
  scopeSlot: { display: 'flex', justifyContent: 'flex-end', minWidth: 132 },
  capLbl: { fontSize: 12, color: C.limited, fontWeight: 600, whiteSpace: 'nowrap' },
  capBox: { display: 'inline-flex', alignItems: 'center', background: '#fbf1da', border: '1px solid #eeddb4', borderRadius: 9, overflow: 'hidden' },
  capInput: { width: 54, border: 'none', background: 'transparent', padding: '8px 4px 8px 10px', fontSize: 14, fontWeight: 700, color: C.limited, textAlign: 'right', outline: 'none' },
  capUnit: { padding: '0 11px 0 3px', fontSize: 13, color: C.limited, fontWeight: 700 },
  capUnitSelect: { border: 'none', background: 'transparent', color: C.limited, fontWeight: 700, fontSize: 13, padding: '0 6px', outline: 'none', cursor: 'pointer' },
  scopeSelect: { width: '100%', background: '#fbf1da', border: '1px solid #eeddb4', borderRadius: 9, color: C.limited, fontWeight: 600, fontSize: 12, padding: '7px 8px', outline: 'none', cursor: 'pointer' },

  legend: { display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 14, fontSize: 12, color: '#8b96a6' },
  dot: { display: 'inline-block', width: 11, height: 11, borderRadius: 3, marginRight: 6, verticalAlign: -1 },
};

export default CampaignAuthorizationDetailPage;
